'use client'

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { BottomSheet, SidebarNavItem } from '@doist/marketist'

import { useGSAP } from '@gsap/react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useTranslation } from 'next-i18next'

import css from './table-of-contents.module.css'

gsap.registerPlugin(ScrollTrigger)

export type TableOfContentsProps = {
    items: { id: string; name: string }[]
    mobileAccessoryView?: React.ReactNode
}

export function TableOfContents({ items, mobileAccessoryView }: TableOfContentsProps) {
    const { t } = useTranslation('common')
    const [activeAnchor, setActiveAnchor] = useState(items[0].id)
    const [showSheet, setShowSheet] = useState(false)

    useEffect(() => {
        const mediaQueryList = window.matchMedia('(max-width: 959px)')

        function handleMediaQueryChange(e: MediaQueryListEvent | MediaQueryList) {
            setShowSheet(e.matches)
        }

        mediaQueryList.addEventListener('change', handleMediaQueryChange)

        handleMediaQueryChange(mediaQueryList)

        return () => {
            mediaQueryList.removeEventListener('change', handleMediaQueryChange)
        }
    }, [])

    // Add triggers for each anchor and update sidebar items accordingly
    useGSAP(() => {
        let prevId: string | null = null
        const triggers = items.map((item) => {
            const trigger = ScrollTrigger.create({
                trigger: document.getElementById(item.id) as HTMLElement,
                start: 'top center',
                onEnter: () => setActiveAnchor(item.id),
                onLeaveBack: (function (prevId) {
                    return () => prevId && setActiveAnchor(prevId)
                })(prevId),
            })
            prevId = item.id
            return trigger
        })

        return () => triggers.forEach((trigger) => trigger.kill())
    })

    const TOC = (
        <ul className={css.tableOfContents}>
            {items.map((item) => (
                <SidebarNavItem
                    key={item.id}
                    active={activeAnchor === item.id}
                    href={`#${item.id}`}
                >
                    {item.name}
                </SidebarNavItem>
            ))}
        </ul>
    )

    return (
        <>
            {TOC}
            {showSheet &&
                createPortal(
                    <BottomSheet title={t('tableOfContents')}>
                        {TOC}
                        {mobileAccessoryView ? (
                            <div className="mbs-24">{mobileAccessoryView}</div>
                        ) : null}
                    </BottomSheet>,
                    document.body,
                )}
        </>
    )
}
